/**
 * College gate — client mirror of the backend services/college_tiers
 * match_requirement, used for the applications-page "Below college bar" flag
 * and the hide-by-default behaviour of a JD's "Require premium" setting.
 *
 * Pure / display-only — never rejects or mutates a candidate. It reads the
 * candidate's precomputed tier (backend serialize_candidate) and the JD's
 * normalized preference, so the two stay consistent without duplicating the
 * taxonomy on the client.
 *
 * @param candidate   { college_tier: 1|2|null, college: string }
 * @param requirement { mode:'any'|'preferred'|'required', tiers:number[], schools:string[] }
 *                    (jobDetails.college_requirement — already normalized by the API)
 * @returns { mode, required, meets, reason }
 */
export function collegeGateStatus(candidate, requirement) {
  const req = requirement && typeof requirement === 'object' ? requirement : {};
  const mode = ['any', 'preferred', 'required'].includes(req.mode) ? req.mode : 'any';
  if (mode === 'any') return { mode, required: false, meets: true, reason: '' };

  const tiers = Array.isArray(req.tiers) ? req.tiers : [];
  const schools = Array.isArray(req.schools) ? req.schools : [];
  const tier = candidate?.college_tier;
  const college = (candidate?.college || '').trim().toLowerCase();

  let meets = false;
  if ((tier === 1 || tier === 2) && tiers.includes(tier)) meets = true;
  if (!meets && college && schools.length) {
    meets = schools.some((s) => {
      const sn = (s || '').trim().toLowerCase();
      return sn && (college.includes(sn) || sn.includes(college));
    });
  }

  const wants = [];
  if (tiers.length) wants.push(tiers.map((t) => `Tier ${t}`).join(' / '));
  if (schools.length) wants.push('a listed school');
  const wantStr = wants.join(' or ') || 'a premium college';
  const collegeLabel = candidate?.college ? `“${candidate.college}”` : 'this candidate’s college';

  const reason = meets
    ? ''
    : `This role ${mode === 'required' ? 'requires' : 'prefers'} ${wantStr}; ${collegeLabel} doesn’t match.`;

  return { mode, required: mode === 'required', meets, reason };
}

/** True when this candidate should be hidden by default (JD requires premium
 *  and this candidate falls short). */
export function isBelowCollegeBar(candidate, requirement) {
  const g = collegeGateStatus(candidate, requirement);
  return g.required && !g.meets;
}
